/**
 * Start a page's code and its first reads before the page asks for them.
 *
 * Every screen used to wait in line: the session, then the page's code, then (React's Suspense throttle) 300 ms,
 * then its data, then the reads that need that data. From Medellín each step is at least 330 ms. This table maps
 * an address to the page that draws it and the reads it opens with, and is run:
 *
 * - at boot (`main.tsx`), for the address being loaded, beside the session read rather than after it;
 * - on intent (`usePrefetchOnIntent`): a pointer resting on a sidebar link or a concept card, or focus on it;
 * - when the browser is idle after the first paint (`preloadLikelyPages`), for the code of each role's usual
 *   next pages.
 *
 * Reads use the hooks' own option objects, so they land under exactly the keys the pages ask for, and nothing
 * here decides what anyone may see: a read the server refuses is dropped quietly (`meta.prefetch`, `main.tsx`),
 * and the session decides what renders.
 */
import { useCallback, useEffect, useRef } from "react";
import type { FetchQueryOptions, QueryClient, QueryKey } from "@tanstack/react-query";
import { canSeeStrategistPayout, isAdminRole, type BriefDetailResponse, type SessionInfo } from "@scandi/shared";
import { sessionKey } from "@/hooks/use-session";
import {
  aiConceptsQuery,
  creativeDetailQuery,
  editorDetailQuery,
  leaderboardQuery,
  newWinnersQuery,
  periodsQuery,
  prizeContestsQuery,
  strategistQuery,
  topVideosQuery,
} from "@/hooks/use-leaderboard";
import { TOP_VIDEOS_PAGE_SIZE } from "@scandi/shared";
import { allBriefsQuery, briefQuery, briefsQuery, lookupsQuery, MY_WORK_LIST, OVERVIEW_LIST } from "@/hooks/use-pipeline";
import { parseRange } from "@/hooks/use-range-params";
import { ApiError, request } from "@/lib/api";
import type { LazyRoute } from "@/lib/lazy-route";
import * as pages from "@/route-pages";

type Role = SessionInfo["role"];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Page = LazyRoute<any>;

/** Marks a read as started ahead of the page: its failure is nobody's to see (`main.tsx`). */
const PREFETCH = { prefetch: true } as const;

/** What an address needs: the pages that draw it, and its first reads. */
interface Target {
  pages: Page[];
  reads?: (queryClient: QueryClient, role: Role) => void;
}

const isTeam = (role: Role) => role !== null && role !== "editor";

function prefetch<TData, TError, TKey extends QueryKey>(
  queryClient: QueryClient,
  options: FetchQueryOptions<TData, TError, TData, TKey>,
): Promise<void> {
  return queryClient.prefetchQuery({ ...options, meta: PREFETCH });
}

/* ------------------------------------------------------------ the reads */

function leaderboardReads(queryClient: QueryClient, role: Role, search: URLSearchParams): void {
  const range = parseRange(search);
  void prefetch(queryClient, leaderboardQuery(range));
  void prefetch(queryClient, newWinnersQuery());
  void prefetch(queryClient, prizeContestsQuery());
  void prefetch(queryClient, aiConceptsQuery());
  void prefetch(queryClient, periodsQuery()).then(() => {
    // The strategist's payout follows the period the board shows: a chosen one, or the one in progress.
    if (role === null || !canSeeStrategistPayout(role)) return;
    const periods = queryClient.getQueryData(periodsQuery().queryKey);
    // A rolling range shows the period in progress ("current"); the previous period is left to the card.
    const index = range.periodIndex ?? (range.preset === "current_period" ? periods?.currentIndex : undefined);
    if (range.preset === "previous_period" || (range.preset === "current_period" && index === undefined)) return;
    void prefetch(queryClient, strategistQuery(index));
  });
}

/**
 * Whether the API reads a concept's storyboard by the concept's ref (`GET /api/pipeline/briefs/:ref/storyboard`
 * and `/editor`). Unknown until the first try; an older API answers its catch-all 404 with no code, and then this
 * tab stops asking and reads by the id once the brief names it, as the pages do.
 */
let byRefRoutes: "unknown" | "yes" | "no" = "unknown";
const byRefInFlight = new Map<string, Promise<ByRefAnswer | null>>();

type StoryboardRead = "doc" | "view" | "editor";
type ByRefAnswer = { storyboardId?: unknown; storyboard?: unknown } & Record<string, unknown>;

/**
 * The read modules, remembered once loaded: a by-id read must be registered before the page's first render
 * asks for the same thing, and awaiting even a loaded module can let that render go first.
 */
type StoryboardHooks = typeof import("@/hooks/use-storyboard");
type EditorAdapter = typeof import("@/features/clip-editor/model/adapter");
let hooksLoaded: StoryboardHooks | null = null;
let adapterLoaded: EditorAdapter | null = null;
const storyboardHooks = (): Promise<StoryboardHooks> =>
  hooksLoaded ? Promise.resolve(hooksLoaded) : import("@/hooks/use-storyboard").then((m) => (hooksLoaded = m));
const editorAdapter = (): Promise<EditorAdapter> =>
  adapterLoaded ? Promise.resolve(adapterLoaded) : import("@/features/clip-editor/model/adapter").then((m) => (adapterLoaded = m));

/**
 * The storyboard read a page opens with. By the id when a card's row or a cached brief names it. Otherwise by
 * the ref, at once, beside the brief: when the brief lands and names the id, the by-id read the page is about to
 * make is registered as waiting on that answer, so the page joins it instead of asking again - the storyboard
 * arrives one round trip after the click rather than two.
 */
function storyboardReads(queryClient: QueryClient, ref: string, read: StoryboardRead, storyboardId?: number | null): void {
  // The read's module, loading while the requests are out.
  const module = read === "editor" ? editorAdapter() : storyboardHooks();
  void module.catch(() => undefined);
  const known =
    storyboardId ?? queryClient.getQueryData<BriefDetailResponse>(briefQuery(ref).queryKey)?.brief.storyboard?.id ?? null;
  if (known !== null) {
    // A by-ref read already on its way (the boot's, before a kept brief was restored) answers this one too.
    void byId(queryClient, known, read, byRefInFlight.get(byRefPath(ref, read)) ?? null);
    return;
  }
  const early = byRefRoutes === "no" ? Promise.resolve(null) : byRef(ref, read);
  void prefetch(queryClient, briefQuery(ref)).then(() => {
    const id = queryClient.getQueryData<BriefDetailResponse>(briefQuery(ref).queryKey)?.brief.storyboard?.id;
    if (id != null) void byId(queryClient, id, read, early);
  });
}

/** The by-id read, answered from the by-ref answer when it is for the same storyboard, else read as usual. */
function byId(queryClient: QueryClient, id: number, read: StoryboardRead, early: Promise<ByRefAnswer | null> | null): void {
  // Synchronously when the module is in, so the read is registered before anything renders.
  if (read === "editor" ? adapterLoaded : hooksLoaded) registerById(queryClient, id, read, early);
  else void (read === "editor" ? editorAdapter() : storyboardHooks()).then(() => registerById(queryClient, id, read, early));
}

function registerById(queryClient: QueryClient, id: number, read: StoryboardRead, early: Promise<ByRefAnswer | null> | null): void {
  const fromRef = async (): Promise<ByRefAnswer | null> => {
    const answer = early ? await early.catch(() => null) : null;
    return answer && answer.storyboardId === id ? answer : null;
  };
  if (read === "editor") {
    const { sessionQuery } = adapterLoaded!;
    const base = sessionQuery(id);
    void prefetch(queryClient, {
      ...base,
      queryFn: async () => {
        const answer = await fromRef();
        if (!answer) return base.queryFn();
        const { storyboardId: _id, ...session } = answer;
        return session as unknown as Awaited<ReturnType<typeof base.queryFn>>;
      },
    });
    return;
  }
  const { storyboardDocQuery, storyboardViewQuery, noteLiveRead } = hooksLoaded!;
  if (read === "view") {
    const base = storyboardViewQuery(id);
    void prefetch(queryClient, {
      ...base,
      queryFn: async (context) => {
        const answer = await fromRef();
        if (!answer) return base.queryFn!(context as never);
        const { storyboardId: _id, ...view } = answer;
        return view as unknown as Awaited<ReturnType<NonNullable<typeof base.queryFn>>>;
      },
    });
    return;
  }
  const base = storyboardDocQuery(id);
  void prefetch(queryClient, {
    ...base,
    queryFn: async (context) => {
      const answer = await fromRef();
      const doc = answer?.storyboard as Awaited<ReturnType<NonNullable<typeof base.queryFn>>> | undefined;
      if (!doc || typeof doc.revision !== "number") return base.queryFn!(context as never);
      noteLiveRead(doc);
      return doc;
    },
  });
}

function byRefPath(ref: string, read: StoryboardRead): string {
  return read === "editor"
    ? `/api/pipeline/briefs/${encodeURIComponent(ref)}/editor?lean=1`
    : `/api/pipeline/briefs/${encodeURIComponent(ref)}/storyboard?${read === "view" ? "view=1" : "live=1"}&lean=1`;
}

function byRef(ref: string, read: StoryboardRead): Promise<ByRefAnswer | null> {
  const path = byRefPath(ref, read);
  const running = byRefInFlight.get(path);
  if (running) return running;
  const done = request<ByRefAnswer>(path).then(
    (answer) => {
      byRefRoutes = "yes";
      return answer;
    },
    (error: unknown) => {
      if (error instanceof ApiError && error.status === 404 && error.code === null) byRefRoutes = "no";
      return null;
    },
  );
  byRefInFlight.set(path, done);
  // Forgotten once settled, so a later read is fresh; the by-id cache keeps the answer meanwhile.
  void done.then(() => byRefInFlight.delete(path));
  return done;
}

/* ------------------------------------------------------------ the table */

const CONCEPT = /^\/pipeline\/b\/([^/]+)(\/.*)?$/;

function targetFor(pathname: string, search: URLSearchParams, role: Role, storyboardId?: number | null): Target | null {
  const path = pathname.replace(/\/+$/, "") || "/";

  if (path === "/" || path === "/leaderboard") {
    return { pages: [], reads: (qc, r) => leaderboardReads(qc, r, path === "/" ? new URLSearchParams() : search) };
  }
  // The ranking and its drill-downs read with the range in the address, so a hover over a row has the
  // page's answer in the cache by the time the click lands.
  if (path === "/videos") {
    return {
      pages: [pages.TopVideosPage],
      reads: (qc) => {
        void prefetch(qc, topVideosQuery(parseRange(search), TOP_VIDEOS_PAGE_SIZE));
        void prefetch(qc, aiConceptsQuery());
      },
    };
  }
  const editor = /^\/editors\/(\d+)$/.exec(path);
  if (editor) {
    return {
      pages: [pages.EditorDetailPage],
      reads: (qc) => void prefetch(qc, editorDetailQuery(Number(editor[1]), parseRange(search))),
    };
  }
  const creative = /^\/creatives\/([^/]+)$/.exec(path);
  if (creative) {
    return {
      pages: [pages.CreativeDetailPage],
      reads: (qc) => void prefetch(qc, creativeDetailQuery(decodeURIComponent(creative[1]!), parseRange(search))),
    };
  }

  const myWork: Target = { pages: [pages.AssignmentsPage], reads: (qc) => void prefetch(qc, briefsQuery(MY_WORK_LIST)) };
  const overview: Target = {
    pages: [pages.ConceptsOverviewPage],
    reads: (qc) => {
      void qc.prefetchInfiniteQuery({ ...allBriefsQuery(OVERVIEW_LIST), meta: PREFETCH });
      void prefetch(qc, lookupsQuery());
      void prefetch(qc, periodsQuery());
    },
  };
  // The bare pipeline address and an editor's list send each role to its own page.
  if (path === "/pipeline") return role === null ? null : role === "editor" ? myWork : overview;
  if (path === "/pipeline/mi-trabajo") return role === null || role === "editor" ? myWork : overview;
  if (path === "/pipeline/conceptos") return overview;
  if (path === "/pipeline/presentaciones" || path === "/artefactos") return { pages: [pages.ArtifactsPage] };
  if (/^\/artefactos\/\d+$/.test(path)) return { pages: [pages.ArtifactViewerPage] };
  if (path === "/master" || path.startsWith("/master/")) return { pages: [pages.MasterPage] };

  const concept = CONCEPT.exec(path);
  if (concept) {
    const ref = decodeURIComponent(concept[1]!);
    const rest = concept[2] ?? "";
    const brief = (qc: QueryClient) => void prefetch(qc, briefQuery(ref));
    if (rest === "") {
      return {
        pages: [pages.ConceptPage],
        reads: (qc, r) => {
          brief(qc);
          // The team reads the live document, an editor the published view; unknown until the session is in,
          // when only the read's module is loaded, ready for it.
          if (r !== null) storyboardReads(qc, ref, isTeam(r) ? "doc" : "view", storyboardId);
          else void storyboardHooks().catch(() => undefined);
        },
      };
    }
    if (rest === "/presentacion") {
      return { pages: [pages.PresentationSummaryPage], reads: (qc) => (brief(qc), storyboardReads(qc, ref, "doc", storyboardId)) };
    }
    if (rest === "/presentacion/editor") {
      return { pages: [pages.ClipEditorPage], reads: (qc) => (brief(qc), storyboardReads(qc, ref, "editor", storyboardId)) };
    }
    if (rest.startsWith("/presentacion/l/")) {
      const page = role === null ? null : role === "editor" ? pages.EditorLinePage : pages.LinePage;
      return { pages: page ? [page] : [], reads: brief };
    }
    if (rest === "/deck") return { pages: [pages.DeckPage], reads: (qc) => (brief(qc), storyboardReads(qc, ref, "view", storyboardId)) };
    return null;
  }

  if (path === "/recursos") return { pages: [pages.ResourcesPage] };
  if (path === "/ideas" || path.startsWith("/ideas/")) return { pages: [pages.IdeasRoutes] };
  if (path === "/biblioteca" || path === "/biblioteca/voces") {
    return {
      pages: [pages.LibraryPage],
      reads: (qc, r) => {
        void import("@/hooks/use-generation").then((m) => {
          void prefetch(qc, m.libraryImagesQuery());
          if (r !== null && r !== "editor") void prefetch(qc, m.voicesQuery());
        });
      },
    };
  }

  if (path === "/estudio") return { pages: [pages.StudioLayout, pages.StudioHome] };
  if (path.startsWith("/estudio/guion")) return { pages: [pages.StudioLayout, pages.StudioChatPage] };
  if (path === "/estudio/trabajos") return { pages: [pages.StudioLayout, pages.StudioJobsPage] };
  if (path === "/estudio/trabajos/nuevo") return { pages: [pages.StudioLayout, pages.StudioNewJobPage] };
  if (/^\/estudio\/trabajos\/[^/]+\/presentacion$/.test(path)) return { pages: [pages.StudioJobPresentationPage] };
  if (path.startsWith("/estudio/trabajos/")) return { pages: [pages.StudioLayout, pages.StudioJobPage] };

  if (path === "/coach") return { pages: [pages.AgentChatPage] };
  if (path.startsWith("/settings")) return { pages: [pages.SettingsPage] };
  if (path.startsWith("/coach/sessions")) return { pages: [pages.AgentSessionsPage] };

  const admin: Record<string, Page> = {
    "/admin/users": pages.UsersPage,
    "/admin/api-keys": pages.ApiKeysPage,
    "/admin/actividad": pages.ActivityPage,
    "/admin/gasto": pages.SpendPage,
    "/admin/traducciones": pages.TranslationsPage,
    "/admin/agentes": pages.AgentsAdminPage,
    "/admin/prompt-chat": pages.PromptChatPage,
  };
  const page = admin[path];
  return page ? { pages: [page] } : null;
}

function roleOf(queryClient: QueryClient): Role {
  const session = queryClient.getQueryData<SessionInfo>(sessionKey);
  return session?.authenticated ? session.role : null;
}

/**
 * Load an address's page code and start its first reads. `storyboardId` is passed by a concept card, whose row
 * already names the storyboard. Safe to call often: loaded code and fresh reads are not fetched again.
 */
export function prefetchRoute(
  queryClient: QueryClient,
  href: string,
  options: { role?: Role; storyboardId?: number | null; code?: boolean; reads?: boolean } = {},
): void {
  let url: URL;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return;
  }
  if (url.origin !== window.location.origin) return;
  const role = options.role !== undefined ? options.role : roleOf(queryClient);
  const target = targetFor(url.pathname, url.searchParams, role, options.storyboardId);
  if (!target) return;
  if (options.code !== false) for (const page of target.pages) void page.preload();
  if (options.reads !== false) target.reads?.(queryClient, role);
}

/**
 * Boot: the page being loaded, beside the session read. The parts that depend on the role (an editor's line
 * page or a strategist's, the payout card) go out again once the session is in; whatever already went out is
 * not asked twice.
 */
export function prefetchBoot(queryClient: QueryClient, session: Promise<unknown>): void {
  const href = window.location.pathname + window.location.search;
  // The reads go out before the session answers only in a browser someone was recently signed in on (the
  // app stamps it while in use, `app.tsx`); a signed-out visitor would only collect refusals.
  prefetchRoute(queryClient, href, { role: null, reads: wasSignedInRecently() });
  void session.then(
    () => {
      const role = roleOf(queryClient);
      if (role !== null) prefetchRoute(queryClient, href, { role });
    },
    () => undefined,
  );
}

/** Whether this browser had the platform open, signed in, within the last week (`scandi.lastOpenAt`, app.tsx). */
function wasSignedInRecently(): boolean {
  try {
    const last = Number(window.localStorage.getItem("scandi.lastOpenAt"));
    return Boolean(last) && Date.now() - last < 7 * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

/**
 * After the kept reads are restored (`lib/persist-cache.ts`), just before the first render: a restored brief
 * names its storyboard, so the by-id read the page is about to make is joined to the by-ref read already out.
 */
export function prefetchAfterRestore(queryClient: QueryClient): void {
  const role = roleOf(queryClient);
  if (role !== null) prefetchRoute(queryClient, window.location.pathname + window.location.search, { role, code: false });
}

/* ------------------------------------------------------------ intent */

/** Whether prefetching on intent is worth it here: a pointer that hovers, and no request to save data. */
function intentAllowed(): boolean {
  if (typeof window === "undefined") return false;
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  if (connection?.saveData) return false;
  return true;
}

const HOVER_MS = 80;

/**
 * Pointer and focus handlers that prefetch `href` once the pointer has rested on it for a moment (80 ms, so a
 * pointer crossing the sidebar does not fetch every page on its way) or as soon as it takes focus. On a touch
 * screen the press itself is the first sign, which is still ahead of the click.
 */
export function usePrefetchOnIntent(queryClient: QueryClient, href: string, options: { storyboardId?: number | null } = {}) {
  const timer = useRef<number | null>(null);
  const { storyboardId } = options;
  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
  }, []);
  const go = useCallback(() => {
    if (intentAllowed()) prefetchRoute(queryClient, href, { storyboardId });
  }, [queryClient, href, storyboardId]);
  const onPointerEnter = useCallback(
    (event: React.PointerEvent) => {
      if (event.pointerType === "touch") return;
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(go, HOVER_MS);
    },
    [go],
  );
  const onPointerLeave = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  }, []);
  return { onPointerEnter, onPointerLeave, onFocus: go, onTouchStart: go };
}

/* ------------------------------------------------------------ idle */

/** The code each role usually opens next. */
function likelyPages(role: Role): Page[] {
  if (role === "editor") return [pages.AssignmentsPage, pages.ConceptPage, pages.PresentationSummaryPage, pages.EditorLinePage, pages.ClipEditorPage];
  if (role === "strategist") return [pages.ConceptsOverviewPage, pages.ConceptPage, pages.PresentationSummaryPage, pages.LinePage, pages.LibraryPage];
  if (isAdminRole(role)) return [pages.ConceptsOverviewPage, pages.ConceptPage, pages.PresentationSummaryPage, pages.LibraryPage, pages.SettingsPage];
  return [pages.TopVideosPage, pages.EditorDetailPage];
}

/**
 * After the first paint, when the browser has nothing else to do: the code of the role's usual next pages, one
 * at a time, so the first click on each needs no JavaScript from the network. Never on a connection that asked
 * to save data.
 */
export function preloadLikelyPages(role: Role): void {
  if (!intentAllowed()) return;
  const queue = likelyPages(role).filter((page) => !page.loaded);
  const idle = (callback: () => void): void => {
    if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(callback, { timeout: 4000 });
    else setTimeout(callback, 1500);
  };
  const next = () => {
    const page = queue.shift();
    if (!page) return;
    void page.preload().then(() => idle(next));
  };
  // Well after the page's own reads and pictures have gone out.
  window.setTimeout(() => idle(next), 2500);
}
